import { useCallback } from 'react';
import { RESERVATION_POLICY } from '@wineryxplorer/shared';
import type { ReservationPolicy } from '@wineryxplorer/shared';
import { ValidationError } from '../ui/ValidationError';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface ReservationPolicySelectorProps {
  value?: ReservationPolicy;
  onChange: (value: ReservationPolicy) => void;
  error?: string | string[];
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
}

const RESERVATION_POLICY_LABELS: Record<ReservationPolicy, string> = {
  required: 'Reservation required — walk-ins not accepted',
  recommended: 'Reservations recommended — drop-ins welcome but advance booking suggested',
  'drop-ins-welcome': 'Drop-ins welcome — no reservation needed',
};

export const ReservationPolicySelector = ({
  value,
  onChange,
  error,
  disabled = false,
  loading = false,
  required = false,
}: ReservationPolicySelectorProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value as ReservationPolicy);
    },
    [onChange]
  );

  const hasError = Boolean(error);

  return (
    <div className="space-y-3">
      <label htmlFor="reservationPolicy" className="block text-sm font-medium text-gray-700 mb-1">
        Reservation Policy
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <select
          id="reservationPolicy"
          value={value ?? 'recommended'}
          onChange={handleChange}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 bg-gray-50 touch-manipulation transition-colors ${
            hasError
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-info-500 focus:border-info-500'
          } ${
            disabled || loading
              ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
              : 'hover:border-gray-400'
          }`}
          style={{ minHeight: '44px' }}
          aria-invalid={hasError}
          aria-describedby={hasError ? 'reservationPolicy-error' : undefined}
        >
          {RESERVATION_POLICY.map((policy) => (
            <option key={policy} value={policy}>
              {RESERVATION_POLICY_LABELS[policy]}
            </option>
          ))}
        </select>

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <LoadingSpinner size="sm" className="text-gray-400" />
          </div>
        )}
      </div>

      <ValidationError error={error} className="mt-1" />
    </div>
  );
};
